import { useState, useEffect, useMemo } from "react";
import { Button } from "V2.0/common/button";
import { LanguageIcon, PencilIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Dialog, DialogTitle, DialogBody } from "V2.0/common/dialog";
import TranscriptionSettings, {
  SONIOX_TRANSCRIPTION_PREFIX,
} from "./TranscriptionSettings";
import { DialogTitleWithClose } from "V2.0/components/DialogTitleWithClose";
import { getTranscriptionLanguage } from "./models/transcriptionSettings";
import { SonioxLanguageCode } from "V2.0/HomePages/Assistants/TranscriptionSettingsWithProviders/models/soniox";

function Transcription(props) {
  const { settings, syncAssistant, workspaceId, assistantId, locked } = props;
  const [open, setOpen] = useState(false);

  const sonioxBaseSet = useMemo(
    () => new Set<string>(Object.values(SonioxLanguageCode)),
    []
  );

  const languages = {
    "ar-AE": { label: "Arabic (UAE)", transcriptionProvider: "azure" },
    bg: { label: "Bulgarian" },
    "bn-IN": {
      label: "Bengali (India)",
      transcriptionProvider: "azure",
    },
    ca: { label: "Catalan" },
    cs: { label: "Czech" },
    da: { label: "Danish" },
    "da-DK": { label: "Danish (Denmark)" },
    nl: { label: "Dutch" },
    en: { label: "English" },
    "en-US": { label: "English (United States)" },
    "en-AU": { label: "English (Australia)" },
    "en-GB": { label: "English (United Kingdom)" },
    "en-NZ": { label: "English (New Zealand)" },
    "en-IN": { label: "English (India)" },
    et: { label: "Estonian" },
    fi: { label: "Finnish" },
    "nl-BE": { label: "Flemish" },
    fr: { label: "French" },
    "fr-CA": { label: "French (Canada)" },
    de: { label: "German" },
    "de-CH": { label: "German (Switzerland)" },
    el: { label: "Greek" },
    hi: { label: "Hindi" },
    hu: { label: "Hungarian" },
    "he-IL": {
      label: "Hebrew (Israel)",
      transcriptionProvider: "azure",
    },
    id: { label: "Indonesian" },
    it: { label: "Italian" },
    ja: { label: "Japanese" },
    "kn-IN": {label: "Kannada", transcriptionProvider: "azure"},
    ko: { label: "Korean" },
    "ko-KR": { label: "Korean (South Korea)" },
    lv: { label: "Latvian" },
    lt: { label: "Lithuanian" },
    ms: { label: "Malay" },
    "ml-IN": {label: "Malayalam", transcriptionProvider: "azure"},
    "mr-IN": {label: "Marathi", transcriptionProvider: "azure"},
    no: { label: "Norwegian" },
    pl: { label: "Polish" },
    pt: { label: "Portuguese" },
    "pt-BR": { label: "Portuguese (Brazil)" },
    ro: { label: "Romanian" },
    ru: { label: "Russian" },
    sk: { label: "Slovak" },
    es: { label: "Spanish" },
    "es-419": { label: "Spanish (Latin America)" },
    sv: { label: "Swedish" },
    "sv-SE": { label: "Swedish (Sweden)" },
    "ta-IN": {label: "Tamil", transcriptionProvider: "azure"},
    "te-IN": {label: "Telugu", transcriptionProvider: "azure"},
    th: { label: "Thai" },
    "th-TH": { label: "Thai (Thailand)" },
    tr: { label: "Turkish" },
    uk: { label: "Ukrainian" },
    "ur-IN": { label: "Urdu", transcriptionProvider: "azure" },
    vi: { label: "Vietnamese" },
    "zh-CN": { label: "Chinese (Mandarin, Simplified)" },
    "zh-TW": { label: "Chinese (Mandarin, Traditional)" },
    "multi-2": {
      label: "Multilingual (Spanish/English)",
      transcriptionModel: "nova-2",
    },
    "multi-3": {
      label:
        "Multilingual (English, Spanish, French, German, Hindi, Russian, Portuguese, Japanese, Italian, and Dutch)",
      transcriptionModel: "nova-3",
    },
  };

  const transcriptionLanguageOptions = useMemo(() => {
    const stateOfTheArtOptions = [];
    const legacyOptions = [];
    const seenSonioxBases = new Set<string>();

    Object.keys(languages).forEach((languageId) => {
      const label = languages[languageId]?.label;
      const baseLanguage = languageId.split("-")[0];

      // Every language stays available under Legacy with its original
      // (Deepgram / Azure) behaviour.
      legacyOptions.push({
        id: `legacy-${languageId}`,
        label,
        value: languageId,
      });

      // Languages Soniox can transcribe are additionally offered under State of
      // the Art, where selecting them switches the provider to Soniox.
      // Old condition (caused duplicate base-language entries, e.g. hi-IN/hi-Latn
      // both showing as "Hindi"): if (sonioxBaseSet.has(baseLanguage)) {
      if (sonioxBaseSet.has(baseLanguage) && !seenSonioxBases.has(baseLanguage)) {
        seenSonioxBases.add(baseLanguage);
        stateOfTheArtOptions.push({
          id: `soniox-${languageId}`,
          label,
          value: `${SONIOX_TRANSCRIPTION_PREFIX}${languageId}`,
        });
      }
    });

    return [
      {
        label: "State of the Art",
        sticky: true,
        options: stateOfTheArtOptions,
      },
      { label: "Legacy", sticky: true, options: legacyOptions },
    ];
  }, [sonioxBaseSet]);

  const optionByValue = useMemo(() => {
    const map = new Map();
    transcriptionLanguageOptions.forEach((group) => {
      group.options.forEach((option) => map.set(option.value, option));
    });
    return map;
  }, [transcriptionLanguageOptions]);

  const selectedLanguages = useMemo(() => {
    const provider = settings?.transcriptionProvider;
    if (!provider) return [];

    // Soniox-backed transcription is stored as the "custom" provider; "soniox"
    // is still accepted for assistants saved via the newer dialog.
    if (provider === "custom" || provider === "soniox") {
      const stateOfTheArtOptions =
        transcriptionLanguageOptions.find(
          (group) => group.label === "State of the Art"
        )?.options || [];

      const hints = settings?.sonioxSettings?.languageHints || [];
      const values = [];

      hints.forEach((baseLanguage) => {
        // Stored hints are base codes ("en"); match back to the first State of
        // the Art option sharing that base (e.g. "en" -> "English").
        const match = stateOfTheArtOptions.find((option) => {
          const languageId = option.value.slice(
            SONIOX_TRANSCRIPTION_PREFIX.length
          );
          return languageId.split("-")[0] === baseLanguage;
        });

        if (match && !values.includes(match.value)) values.push(match.value);
      });

      return values;
    }

    // Azure can hold multiple languages; each is a plain Legacy option value.
    if (provider === "azure") {
      const azureLanguages = settings?.azureSTTSettings?.languages || [];
      return azureLanguages.filter((code) => languages[code]);
    }

    // Deepgram (and any other single-language Legacy provider).
    const languageCode = getTranscriptionLanguage(settings);
    return languageCode ? [languageCode] : [];
  }, [settings, transcriptionLanguageOptions]);

  const languageLabel = useMemo(() => {
    if (selectedLanguages.length === 0) return "";

    const labels = selectedLanguages.map(
      (value) => optionByValue.get(value)?.label || ""
    );

    // A selection is always within one section, so show it once in brackets.
    const sectionName = selectedLanguages[0].startsWith(
      SONIOX_TRANSCRIPTION_PREFIX
    )
      ? "State of the Art"
      : "Legacy";

    return `${labels.join(", ")} (${sectionName})`;
  }, [selectedLanguages, optionByValue]);

  return (
    <div className="flex items-center border rounded-lg min-h-10">
      <div className="px-2 border-r flex items-center gap-x-2">
        <LanguageIcon className="size-4"/>
        <p className="text-sm">{languageLabel}</p>
      </div>
      {!locked && (
        <Button
          className="rounded-none"
          plain
          onClick={() => setOpen(true)}
        >
          <PencilIcon />
        </Button>
      )}
      {open && (
        <Dialog
          open={open}
          onClose={() => {
            setOpen(false);
          }}
        >
          <DialogTitle>
            <DialogTitleWithClose
              title="Select Language"
              onClose={() => {
                setOpen(false);
              }}
            />
          </DialogTitle>
          <DialogBody>
            <TranscriptionSettings
              language={selectedLanguages}
              languages={languages}
              languageOptions={transcriptionLanguageOptions}
              sonioxModel={settings?.sonioxSettings?.model}
              workspaceId={workspaceId}
              assistantId={assistantId}
              syncAssistant={syncAssistant}
              onSuccess={() => setOpen(false)}
            />
          </DialogBody>
        </Dialog>
      )}
    </div>
  );
}

export { Transcription };
